import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Text,
    HStack,
    VStack,
    Flex,
    Divider,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tag,
    TableContainer,
    Button,
    Link,
    Alert,
    AlertIcon,
    Center,
    Spinner,
} from '@chakra-ui/react';
import { CheckIcon, CopyIcon } from '@chakra-ui/icons';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import Header from '../../components/Header';
import usePagePermissions from '../../hooks/usePagePermissions';
import { fetchWrapper } from '../../helpers';

/**
 * Same base URL as the other API docs (Send / Message / Subscriber all read
 * REACT_APP_SEND_API_URL), so every documented endpoint moves together per
 * environment instead of this page hardcoding a staging host.
 */
const CONTACT_ENDPOINT = `${process.env.REACT_APP_SEND_API_URL || ''}/contact`;

/**
 * Shown until /customer/get-key responds, and if the call fails. Same placeholder
 * style the Send API page uses so the table never renders an empty cell.
 */
const API_KEY_PLACEHOLDER = 'your_api_key_here';

const buildHeaders = (apiKey) => [
    ['x-api-key', apiKey, 'Your workspace API key. Required on every request.', true],
    ['Content-Type', 'application/json', 'Request body format.', false],
];

/* `required` is 'either' for phone and email: the request needs at least one of
   the two, so neither is unconditionally required and neither is truly optional.
   Rendered as "Required*" against the footnote under the table. */
const FIELDS = [
    ['phone', 'String', 'either', 'Full international format. No spaces, dashes or + prefix. E.g. 923001234567'],
    ['name', 'String', false, 'Full name of the contact'],
    ['email', 'String', 'either', 'Valid email address'],
    ['tags', 'String[ ]', false, 'Labels. E.g. ["Newsletter", "Loyalty", "Promotional"]'],
    ['customer_profile', 'Object[ ]', false, 'Custom attributes. Each: {"name": "age", "value": "25"}'],
    ['other_contact', 'Object[ ]', false, 'Extra channels. Each: {"name": "Whatsapp", "value": "+923..."}'],
];

const RESPONSES = [
    ['200 / 201', 'Success', 'green', 'Contact created successfully'],
    ['400', 'Bad Request', 'red', 'Neither phone nor email was provided, or a field has an invalid format'],
    ['401', 'Unauthorized', 'orange', 'Missing or invalid x-api-key header'],
    ['409', 'Conflict', 'blue', 'A contact with this phone number already exists'],
    ['500', 'Server Error', 'red', 'Internal error — retry after a short delay'],
];

/* Both samples take the live key so they are copy-paste runnable, and both build
   the URL from CONTACT_ENDPOINT so they follow the environment with the pill. */
const buildMinRequest = (apiKey) => `curl --location '${CONTACT_ENDPOINT}' \\
--header 'x-api-key: ${apiKey}' \\
--header 'Content-Type: application/json' \\
--data '{"phone": "923001234567"}'`;

const buildFullRequest = (apiKey) => `curl --location '${CONTACT_ENDPOINT}' \\
--header 'x-api-key: ${apiKey}' \\
--header 'Content-Type: application/json' \\
--data '{
  "name": "Sara Khan",
  "phone": "923119876543",
  "email": "sara@example.com",
  "tags": ["Newsletter", "Loyalty"],
  "customer_profile": [{"name": "age", "value": "25"}],
  "other_contact": [{"name": "Whatsapp", "value": "+923119876543"}]
}'`;

const SUCCESS_BODY = `{ "success": true, "contact_id": "ct_8f3a2b1c", "message": "Contact created" }`;
const ERROR_BODY = `{ "success": false, "error": "phone or email is required", "code": 400 }`;

/** Section title — same weight/size as the other API doc pages. */
const SectionTitle = ({ children, mt = 8 }) => (
    <Text fontSize="20px" fontWeight="600" color="#000000" mt={mt} mb={3}>
        {children}
    </Text>
);

/** Table shell — light header row, hover rows, matching Api-Documention.js. */
const DocTable = ({ columns, children }) => (
    <TableContainer
        width="100%"
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
    >
        <Table variant="simple" width="100%">
            <Thead bg="#f7f7f7">
                <Tr>
                    {columns.map(([label, width]) => (
                        <Th
                            key={label}
                            width={width}
                            fontSize="14px"
                            fontWeight="600"
                            color="black"
                            textTransform="none"
                            letterSpacing="normal"
                            py={4}
                        >
                            {label}
                        </Th>
                    ))}
                </Tr>
            </Thead>
            <Tbody fontSize="14px">{children}</Tbody>
        </Table>
    </TableContainer>
);

/**
 * Code block — same rendering as ChannelOptDoc.jsx: black panel with Prism
 * syntax highlighting (prism-tomorrow) and a ghost copy button top-right.
 * Kept identical on purpose so the API documentation pages look like one set.
 */
const LANGUAGE_MAP = {
    bash: 'language-bash',
    json: 'language-json',
};

const CodeBlock = ({ code, language = 'bash', label, section, copiedSection, onCopy }) => {
    const prismLanguage = LANGUAGE_MAP[language] || 'language-markup';

    useEffect(() => {
        Prism.highlightAll();
    }, [code]);

    return (
        <Box mb={5}>
            {label && (
                <Text fontSize="14px" color="gray.700" mb={2}>
                    {label}
                </Text>
            )}
            <Box position="relative">
                <pre style={{
                    backgroundColor: 'black',
                    textShadow: 'none',
                    color: 'white',
                    padding: '0px',
                    borderRadius: '8px',
                    position: 'relative',
                }}>
                    <Button
                        position="absolute"
                        top={3}
                        right={3}
                        size="sm"
                        variant="ghost"
                        colorScheme="whiteAlpha"
                        onClick={() => onCopy(code, section)}
                        zIndex={10}
                    >
                        {copiedSection === section ? <CheckIcon w={3} h={3} /> : <CopyIcon w={3} h={3} />}
                    </Button>
                    <code className={prismLanguage} style={{
                        padding: '15px',
                        display: 'block',
                        backgroundColor: 'black',
                    }}>
                        {code}
                    </code>
                </pre>
            </Box>
        </Box>
    );
};

const ContactApiDoc = ({ header }) => {
    const [copiedSection, setCopiedSection] = useState(null);
    const [apiKey, setApiKey] = useState(API_KEY_PLACEHOLDER);

    const { loading: permissionsLoading, can } = usePagePermissions('documentation/contact-api');

    const contactApiDocPermission = can("contact-api-doc");

    /**
     * Same source as the Send / Variable API pages (Api-Documention.js): the
     * customer's api_key, read via /customer/get-key rather than localStorage,
     * keeps this page showing exactly what those pages show. On failure the
     * placeholder stays, so the docs still read correctly for a user whose
     * session cannot load the key.
     */
    useEffect(() => {
        if (permissionsLoading || !contactApiDocPermission) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchWrapper.get(`${process.env.REACT_APP_API_URL}/customer/get-key?key_type=api_key`);
                if (!cancelled && data?.key) setApiKey(data.key);
            } catch {
                // Keep the placeholder — a missing key must not blank the table.
            }
        })();
        return () => { cancelled = true; };
    }, [permissionsLoading, contactApiDocPermission]);

    const headers = useMemo(() => buildHeaders(apiKey), [apiKey]);
    const minRequest = useMemo(() => buildMinRequest(apiKey), [apiKey]);
    const fullRequest = useMemo(() => buildFullRequest(apiKey), [apiKey]);

    const copyToClipboard = (text, section) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedSection(section);
            setTimeout(() => setCopiedSection(null), 2000);
        });
    };

    if (permissionsLoading) {
        return (
            <>
                <Header header={header} />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header header={header} />
            {contactApiDocPermission ? (
            <BoxWrapper>
                <VStack align="stretch" spacing={8}>
                    {/* Title block */}
                    <Flex justify="center" align="center">
                        <Box w="70%" mt={8}>
                            <Text fontSize="4xl" fontWeight="semibold">Contact API</Text>
                            <Text color="gray.600" fontSize="md" mb={4}>
                                Create a contact in your workspace with a single request. Only phone/email
                                is required — name, tags, custom profile attributes and additional contact
                                channels are all optional.
                            </Text>
                        </Box>
                    </Flex>

                    <Divider bg="#eaeaea" />

                    <Flex justify="center" align="center">
                        <Box style={{ width: '70%' }} pb={16}>
                            {/* Endpoint pill — same treatment as the Send/Message API pages */}
                            <Box my="20px">
                                <HStack>
                                    <Box>
                                        <Tag borderRadius="md" fontSize="12px" fontWeight="600" bg="#ff6900" color="white" my="0px">
                                            Post
                                        </Tag>
                                    </Box>
                                    <Tag bg="#fefce8" color="#a65f00" border="1px solid #fff085">
                                        <Link color="#a65f00" fontSize="16px" fontWeight="400" my="0px" href="#">
                                            {CONTACT_ENDPOINT}
                                        </Link>
                                    </Tag>
                                </HStack>
                            </Box>

                            <Alert status="info" borderRadius="lg" fontSize="14px" mb={2}>
                                <AlertIcon />
                                Only <Text as="span" fontWeight="600" mx={1}>phone/email</Text> is required. All other fields are optional.
                            </Alert>

                            {/* Headers */}
                            <SectionTitle>Headers</SectionTitle>
                            <DocTable columns={[['Header', '220px'], ['Value', '220px'], ['Description', 'auto']]}>
                                {headers.map(([name, value, desc, isApiKey]) => (
                                    <Tr key={name} _hover={{ bg: 'gray.50' }}>
                                        <Td fontWeight="600">{name}</Td>
                                        <Td color="#a65f00">
                                            {value}
                                            {isApiKey && (
                                                <Text fontSize="xs" color="gray.500" mt={1}>
                                                    This is your API Key
                                                </Text>
                                            )}
                                        </Td>
                                        <Td color="gray.700">{desc}</Td>
                                    </Tr>
                                ))}
                            </DocTable>

                            {/* Body params */}
                            <SectionTitle>Body Parameters</SectionTitle>
                            <DocTable columns={[['Field', '190px'], ['Type', '110px'], ['Required', '110px'], ['Description', 'auto']]}>
                                {FIELDS.map(([name, type, required, desc]) => (
                                    <Tr key={name} _hover={{ bg: 'gray.50' }}>
                                        <Td fontWeight="600">{name}</Td>
                                        <Td>{type}</Td>
                                        <Td>
                                            <Tag size="sm" colorScheme={required ? 'green' : 'gray'}>
                                                {required === 'either' ? 'Required*' : required ? 'Required' : 'Optional'}
                                            </Tag>
                                        </Td>
                                        <Td color="gray.700">{desc}</Td>
                                    </Tr>
                                ))}
                            </DocTable>
                            <Text fontSize="13px" color="gray.600" mt={2}>
                                * Send at least one of <Text as="span" fontWeight="600">phone</Text> or{' '}
                                <Text as="span" fontWeight="600">email</Text>. Everything else is optional.
                            </Text>

                            {/* Requests */}
                            <SectionTitle>Request Examples</SectionTitle>
                            <CodeBlock code={minRequest} section="min" label="Minimum — phone only" copiedSection={copiedSection} onCopy={copyToClipboard} />
                            <CodeBlock code={fullRequest} section="full" label="Full payload with all fields" copiedSection={copiedSection} onCopy={copyToClipboard} />

                            {/* Responses */}
                            <SectionTitle>Response Codes</SectionTitle>
                            <Box mb={5}>
                                <DocTable columns={[['Code', '130px'], ['Status', '170px'], ['Meaning', 'auto']]}>
                                    {RESPONSES.map(([code, status, scheme, meaning]) => (
                                        <Tr key={code} _hover={{ bg: 'gray.50' }}>
                                            <Td fontWeight="700">{code}</Td>
                                            <Td><Tag size="sm" colorScheme={scheme}>{status}</Tag></Td>
                                            <Td color="gray.700">{meaning}</Td>
                                        </Tr>
                                    ))}
                                </DocTable>
                            </Box>

                            <CodeBlock code={SUCCESS_BODY} language="json" section="success" label="Success response body" copiedSection={copiedSection} onCopy={copyToClipboard} />
                            <CodeBlock code={ERROR_BODY} language="json" section="error" label="Error response body" copiedSection={copiedSection} onCopy={copyToClipboard} />
                        </Box>
                    </Flex>
                </VStack>
            </BoxWrapper>
            ) : (
                <Center h="400px">
                    <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                            No Access
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            You don't have permission to view this sections.
                        </Text>
                    </Box>
                </Center>
            )}
        </>
    );
};

export default ContactApiDoc;
